import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

/**
 * Lightweight dialog to confirm a bus straight from its card. Collects the
 * driver/operator contact details (required by the backend before a bus can be
 * confirmed) and triggers confirmation. For a fuller edit, use the bus edit
 * dialog instead.
 */
export default function ConfirmBusDialog({
  open,
  onClose,
  journey,
  onConfirm,
  title = "Confirm bus",
  description,
  confirmLabel = "Confirm bus",
  nameLabel = "Contact name",
  numberLabel = "Contact number",
}) {
  const [contactName, setContactName] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setContactName(journey?.contactName ?? "");
    setContactNumber(journey?.contactNumber ?? "");
    setSaving(false);
  }, [open, journey]);

  const canConfirm = useMemo(
    () =>
      String(contactName || "").trim() !== "" &&
      String(contactNumber || "").trim() !== "",
    [contactName, contactNumber]
  );

  const handleConfirm = async () => {
    if (!canConfirm || saving) return;

    setSaving(true);
    try {
      await onConfirm?.({
        contactName: contactName.trim(),
        contactNumber: contactNumber.trim(),
      });
      onClose?.();
    } catch {
      // Handler surfaces its own error toast; keep the dialog open to retry.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            {description ||
              `Add the driver/operator contact details to confirm${
                journey?.groupName ? ` “${journey.groupName}”` : " this bus"
              }.`}
          </Typography>

          <TextField
            label={nameLabel}
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            autoFocus
            fullWidth
          />
          <TextField
            label={numberLabel}
            value={contactNumber}
            onChange={(e) => setContactNumber(e.target.value)}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleConfirm}
          disabled={!canConfirm || saving}
        >
          {saving ? "Confirming…" : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
